// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

/* tslint:disable */
/* eslint-disable */
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, filter, map, of } from 'rxjs';
import { PortalWebAppConfiguration } from 'src/app/api/remote-api/.portal.generated/models';
import {
  APICallState,
  STATIC_API,
  UNKEYED_API,
  VoidIfEmpty,
} from 'src/app/modules/shared/utils/api-call-state.util';
import { environment } from 'src/environments/environment';
import { StrictHttpResponse } from '../../../api/remote-api/.portal.generated/strict-http-response';
import { PortalWebAppConfigurationService } from '../../portal/services/portal-web-app-configuration.service';

/**
 * Maps a API endpoint from generated bindings in another servie
 */
@Injectable({ providedIn: 'root' })
export class MapApiService {
  constructor(
    private http: HttpClient,
    private portalWebAppConfigurationService: PortalWebAppConfigurationService // @Optional() // @Inject(CONFIG_ENDPOINT_TOKEN) // private endpointConfig?: ApiEndpointConfig
  ) {}

  public mapEndpoint = this.generateEndpointMapper();

  /**
   * Usage: myEndpoint = mapApiService.mapEndpoint(getMyData, { ...options });
   * @param request request method from generated api folder
   * @param options additional options
   * @returns object with observables for data$, loading$ and error$ states
   */
  private generateEndpointMapper(apiRootUrl = environment.apiRootOverride) {
    return <
      TParams,
      TData,
      TParamsPossiblyVoid extends VoidIfEmpty<NonNullable<TParams>>,
      TParamsKeyer extends VoidIfEmpty<TParamsInput>,
      TDataPostProcessed = TData,
      TParamsInput = TParamsPossiblyVoid,
      TParamsInputProcessed extends TParamsPossiblyVoid = TParamsPossiblyVoid
    >(
      /**
       * Generated typed API call to bind.
       * Takes any function from ../_services/**.generated/fn/**
       */
      request: ((
        http: HttpClient,
        rootUrl: string,
        params: TParams,
        context?: HttpContext
      ) => Observable<StrictHttpResponse<TData>>) & {
        ROOTURL: string;
        PATH: string;
      },
      options: {
        /**
         * Use this to declare and transform custom parameters
         * to the shape that is expected by 'request'.
         * Usefull to add default values
         * @param params
         * @returns
         */
        processParams?: (params: TParamsInput) => TParamsInputProcessed;

        /**
         * Use this to attach rxjs operators to the result of the api call.
         * You will have access to the returned data and are able to use it
         * or even modify it using map operations
         * @param apiResult Result of the API call
         * @param apiParamsUntransformed Original parameters that were passed to processParams
         * @param apiParamsTransformed Final raw parameters that were returnde from processParams and used to invoke the actual api call
         * @returns
         */
        processResult?: (
          apiResult: Observable<TData>,
          apiParamsUntransformed: TParamsInput,
          apiParamsTransformed: TParams
        ) => Observable<TDataPostProcessed>;

        /**
         * Used to provide an optional catchError operator which is automatically
         * attached to the request observable.
         * Equivalent to: processResult(result => result.pipe(catchError))
         * @param error Error from API
         * @param cought Result payload from API
         */
        catchError?: (
          error: HttpErrorResponse,
          caught: Observable<TData | undefined>
        ) => Observable<TData | undefined>;

        /**
         * Allwos to provide custom api cacheKeying method based on parameters
         * UNKEYED_API: Every invocation of .request() will lead to a new api request
         * STATIC_API: Data is expected to not change over session in app -> only first request leads to api request
         *
         * Custom:
         * You can provide a function that maps the inputParams to a string.
         * The first request is always performed.
         * Everytime a request is issued by the app that string is computed using the current parameters.
         * If resulting string (cacheKey) matches the previous one NO REQUEST IS MADE and EXISTING DATA IS USED
         * You can use paramObjectKeyerDefault to get a default key mapping (i.e. JSON.stringify(params))
         */
        customCacheKey?:
          | typeof UNKEYED_API
          | typeof STATIC_API
          | ((params: TParamsKeyer) => string);
      } = {}
    ) => {
      let {
        processResult,
        processParams,
        customCacheKey: keyer,
        catchError: _catchError,
      } = options;
      // by default the hooks do nothing
      if (!processResult) {
        processResult = (result) =>
          result as unknown as Observable<TDataPostProcessed>;
      }
      if (!processParams) {
        processParams = (params) => params as unknown as TParamsInputProcessed;
      }
      if (!_catchError) {
        _catchError = (error: HttpErrorResponse) => of(undefined);
      }
      if (!keyer) {
        keyer = UNKEYED_API;
      }
      return new APICallState(
        (params: TParamsInput) => {
          // generated api do not suppor omitting params
          const processedParams = (processParams!(params) || {}) as TParams;

          //
          const generatedApiCall = request(
            this.http,
            translateApiRoot(
              apiRootUrl || request.ROOTURL,
              this.portalWebAppConfigurationService.getPortalWebAppConfiguration()
            ),
            processedParams
          ).pipe(
            filter((r) => r instanceof HttpResponse),
            map((r) => r as StrictHttpResponse<TData>),
            map((r) => r.body),
            map((r) => {
              return r;
            })
            // TODO (we already have a catch error in the api-call-state-utils)
            // catchError(_catchError!)
          );
          return processResult!(generatedApiCall, params, processedParams);
        },
        keyer,
        `${apiRootUrl ?? request.ROOTURL}${request.PATH}`
      );
    };
  }
}

function translateApiRoot(root: string, config: PortalWebAppConfiguration) {
  const translations = {
    '/log': config.logCompanionUrl,
  };
  Object.entries(translations).forEach(([key, value]) => {
    if (key.replace(/\/$/, '') === root) {
      root = value.replace(/\/$/, '');
    }
  });
  return root;
}

/**
 * Use this for APIs that only should make new requests
 * in case the serial or material number changes
 */
export function keyBySerialAndMaterialNumber(params: {
  systemSerialNumber: string;
  systemMaterialNumber: number;
}) {
  return JSON.stringify({
    snr: params.systemSerialNumber,
    matnr: parseInt(params.systemMaterialNumber as unknown as string), // <-
  });
}
